import React from "react";

interface PaginationProps {
  currentPage: number | string;
  totalPages: number | string;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  // Defensive: make sure we are working with numbers (avoid string-date bugs)
  const current = Number(currentPage);
  const total = Number(totalPages);

  if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 1)
    return null;

  const generatePages = () => {
    const pages: (number | string)[] = [];

    const maxVisible = 5; // number of pages in the "middle" window
    const side = Math.floor(maxVisible / 2);

    // Always include first and last
    pages.push(1);

    if (total <= maxVisible + 2) {
      // small number of pages -> just list them all
      for (let i = 2; i <= total - 1; i++) pages.push(i);
      if (total > 1) pages.push(total);
      // final filter for safety
      return Array.from(new Set(pages));
    }

    // compute middle window bounds (only for pages 2 .. total-1)
    let start = current - side;
    let end = current + side;

    // clamp to valid inner range (2 .. total-1)
    if (start < 2) {
      start = 2;
      end = start + maxVisible - 1;
    }
    if (end > total - 1) {
      end = total - 1;
      start = end - maxVisible + 1;
      if (start < 2) start = 2;
    }

    // If there is a gap between 1 and start, add ellipsis
    if (start > 2) pages.push("...");

    // add middle pages (explicit loop, avoids overlap with 1 and total)
    for (let i = start; i <= end; i++) {
      // safety: only include valid inner pages
      if (i > 1 && i < total) pages.push(i);
    }

    // If there is a gap between end and total, add ellipsis
    if (end < total - 1) pages.push("...");

    // always push last page
    pages.push(total);

    // FINAL SAFETY: remove exact duplicates while preserving order
    const seen = new Set<number | string>();
    const deduped: (number | string)[] = [];
    for (const p of pages) {
      if (!seen.has(p)) {
        seen.add(p);
        deduped.push(p);
      }
    }

    return deduped;
  };

  const pageList = generatePages();

  return (
    <div className="flex items-center justify-center gap-2 mb-4 mt-2">
      <button
        onClick={() => onPageChange(Math.max(1, current - 1))}
        disabled={current === 1}
        className={`px-3 py-1 rounded border text-sm ${
          current === 1
            ? "text-gray-400 border-gray-300 cursor-not-allowed"
            : "hover:bg-gray-200"
        }`}
      >
        Prev
      </button>

      {pageList.map((item, idx) =>
        item === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-3 py-1 text-gray-600 select-none"
          >
            ...
          </span>
        ) : (
          <button
            // unique key even if numbers repeat unexpectedly elsewhere
            key={`page-${String(item)}-${idx}`}
            onClick={() => onPageChange(Number(item))}
            className={`px-3 py-1 rounded text-sm border ${
              current === Number(item)
                ? "bg-blue-600 text-white border-blue-600"
                : "hover:bg-gray-200"
            }`}
          >
            {item}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(Math.min(total, current + 1))}
        disabled={current === total}
        className={`px-3 py-1 rounded border text-sm ${
          current === total
            ? "text-gray-400 border-gray-300 cursor-not-allowed"
            : "hover:bg-gray-200"
        }`}
      >
        Next
      </button>
    </div>
  );
}
